// 🔎 누락 청구 감지 (기공소 대표 전용) — 2026-08-17 대표 확정
// "자동 판정기가 아니라, 의심 건을 소장에게 보여주는 필터 + 소장이 이유를 고르는 화면".
//   시스템은 후보(의심)만 제시하고, 판단은 사람이 한다. 발행·정산·금액계산은 여기서 하지 않는다.
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Search, RefreshCw, AlertCircle, Info, RotateCcw } from 'lucide-react';
import PageHeader from './common/PageHeader';
import {
    detectMissingClaims, DISPOSITIONS, DISPOSITION_LABEL,
    buildReviewIndex, reviewOfRow, saveReview, deleteReview,
} from '../utils/missingClaims';

const fmt = (n) => (Math.round(n) || 0).toLocaleString('ko-KR');
// 지난달 1일 (첫 진입 시 이번 달·지난달을 함께 훑는다)
const prevMonthStart = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function MissingClaims({ user }) {
    const uid = user?.companyId || auth.currentUser?.uid;
    const isStaff = !!user?.companyId;   // 직원 계정(회사 소속) — 대표 전용 화면이라 접근 제한

    const [from, setFrom] = useState(prevMonthStart());
    const [to, setTo] = useState(today());
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);          // detectMissingClaims 결과
    const [reviewMap, setReviewMap] = useState(new Map()); // caseKey(메모리 전용) → { disposition, docId, legacy }
    const [openClinics, setOpenClinics] = useState(new Set()); // 치과별 접기 — 기본 접힘 (2026-08-17 대표 지시)
    const [saving, setSaving] = useState(null);            // 저장 중인 caseKey

    const run = useCallback(async () => {
        if (!uid || isStaff) return;
        setLoading(true);
        try {
            // 1) 의심 후보 계산 (조회·매칭·추정만)
            const res = await detectMissingClaims({ labId: uid, from, to });
            // 2) 이미 소장이 확인한 건(disposition) 불러오기 — 처음부터 '처리됨'으로 분리
            //    새 형식({labId}_{parsedCaseId}, 환자명 없음)과 옛 형식(caseKey) 모두 인정 (2026-10-23)
            const rSnap = await getDocs(query(collection(db, 'missingClaimReviews'), where('labId', '==', uid)));
            const rix = buildReviewIndex(rSnap.docs.map(d => ({ id: d.id, data: d.data() })));
            const map = new Map();
            (res.suspects || []).forEach(s => {
                const rv = reviewOfRow(rix, { caseIds: s.caseIds || [s.parsedCaseId], caseKey: s.caseKey });
                if (rv) map.set(s.caseKey, rv);
            });
            setResult(res);
            setReviewMap(map);
        } catch (e) {
            console.error('누락 청구 감지 실패:', e?.code || e?.message || e);
            alert('조회에 실패했습니다. 잠시 후 다시 시도해 주세요.');
        }
        setLoading(false);
    }, [uid, from, to, isStaff]);

    useEffect(() => { if (uid && !isStaff) run(); /* eslint-disable-next-line */ }, [uid]);

    // 소장 확인 저장 — missingClaimReviews/{labId}_{parsedCaseId} (환자명 저장 안 함, 2026-10-23)
    const saveDisposition = async (suspect, disposition) => {
        if (!uid) return;
        setSaving(suspect.caseKey);
        try {
            const saved = await saveReview(uid, { ...suspect, review: reviewMap.get(suspect.caseKey) || null }, disposition);
            setReviewMap(prev => { const m = new Map(prev); m.set(suspect.caseKey, { disposition, docId: saved.docId, legacy: false }); return m; });
        } catch (e) {
            console.error('소장 확인 저장 실패:', e?.code || e?.message || e);
            alert('저장에 실패했습니다.');
        }
        setSaving(null);
    };

    // 되돌리기 — 처리됨 → 다시 의심으로
    const undoDisposition = async (suspect) => {
        if (!uid) return;
        setSaving(suspect.caseKey);
        try {
            await deleteReview(uid, { ...suspect, review: reviewMap.get(suspect.caseKey) || null });
            setReviewMap(prev => { const m = new Map(prev); m.delete(suspect.caseKey); return m; });
        } catch (e) {
            console.error('되돌리기 실패:', e?.code || e?.message || e);
            alert('되돌리기에 실패했습니다.');
        }
        setSaving(null);
    };

    if (isStaff) {
        return (
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
                <PageHeader icon={Search} title="누락 청구 감지" />
                <div style={{ textAlign: 'center', padding: 50, color: '#9ca3af', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, lineHeight: 1.7 }}>
                    이 화면은 기공소 대표 계정에서만 볼 수 있습니다.
                </div>
            </div>
        );
    }

    const suspects = result?.suspects || [];
    const pending = suspects.filter(s => !reviewMap.has(s.caseKey));
    const processed = suspects.filter(s => reviewMap.has(s.caseKey));
    const pendingCount = pending.length;
    const pendingEstimate = pending.reduce((a, s) => a + (Number(s.estimate) || 0), 0);
    const pendingUnknown = pending.filter(s => s.estimate == null || s.unpriced).length;

    // 치과별 그룹
    const groups = new Map();
    pending.forEach(s => { if (!groups.has(s.clinicName)) groups.set(s.clinicName, []); groups.get(s.clinicName).push(s); });

    return (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
            <PageHeader icon={Search} title="누락 청구 감지" />
            <p style={{ color: '#64748b', fontSize: 13.5, margin: '4px 0 14px', lineHeight: 1.7 }}>
                작업한 케이스 중 <b>거래명세서로 청구된 흔적이 없는 건</b>을 골라 보여 줍니다.
                자동으로 누락을 확정하지 않습니다 — 각 건은 <b>의심</b>일 뿐이며, 실제 처리는 소장님이 버튼으로 확인해 주세요.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputS} />
                <span style={{ color: '#94a3b8' }}>~</span>
                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputS} />
                <button onClick={run} disabled={loading} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', border: 'none',
                    borderRadius: 8, background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
                }}>
                    <RefreshCw size={14} /> {loading ? '확인 중...' : '조회'}
                </button>
            </div>

            {/* 헤드라인 카드 */}
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 14, padding: '18px 22px', marginBottom: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#9a3412' }}>누락 의심</span>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#7c2d12', fontVariantNumeric: 'tabular-nums' }}>{pendingCount}건</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#9a3412' }}>· 확인 대상 약 {fmt(pendingEstimate)}원(추정)</span>
                <span style={{ fontSize: 12, color: '#b45309' }}>{from} ~ {to}{pendingUnknown > 0 ? ` · 단가 미상 ${pendingUnknown}건` : ''}</span>
                <span style={{ width: '100%', fontSize: 11.5, color: '#b45309', marginTop: 2 }}>※ 확인 전 의심 건들의 추정 합계입니다. 소장님이 확인하면서 줄어듭니다 — 실제 못 받은 금액이 아닙니다.</span>
            </div>

            {/* 신뢰도 안내 — 이름 대조 특성상 오차 가능. 항상 노출한다(과신 방지). */}
            {result && pendingCount > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                    <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>대부분 <b>환자 이름·치식으로 대조</b>한 결과라, 이미 청구했거나 종이로 받은 건이 섞일 수 있습니다. 각 건을 확인하고 아래 버튼으로 표시해 주세요.</span>
                </div>
            )}

            {result === null ? null : pendingCount === 0 ? (
                <div style={{ textAlign: 'center', padding: 44, color: '#059669', background: '#f0fdf4', borderRadius: 12, fontSize: 14, fontWeight: 600 }}>
                    확인이 필요한 누락 의심 건이 없습니다.
                </div>
            ) : (
                [...groups.entries()].map(([clinic, list]) => {
                    // 치과별 접기 (2026-08-17 대표 지시) — 기본 접힘, 치과 이름 줄을 누르면 항목이 펼쳐진다
                    const open = openClinics.has(clinic);
                    const clinicSum = list.reduce((a, s) => a + (Number(s.estimate) || 0), 0);
                    return (
                    <div key={clinic} style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff', marginBottom: 14 }}>
                        <div
                            onClick={() => setOpenClinics(prev => {
                                const n = new Set(prev);
                                if (n.has(clinic)) n.delete(clinic); else n.add(clinic);
                                return n;
                            })}
                            style={{ background: '#f8fafc', padding: '12px 16px', display: 'flex', alignItems: 'baseline', gap: 8, borderBottom: open ? '1px solid #f1f5f9' : 'none', cursor: 'pointer', userSelect: 'none' }}>
                            <span style={{ fontSize: 12, color: '#94a3b8', width: 14 }}>{open ? '▾' : '▸'}</span>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#334155' }}>{clinic}</span>
                            <span style={{ fontSize: 12, color: '#94a3b8' }}>{list.length}건</span>
                            <span style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 700, color: '#7c2d12', fontVariantNumeric: 'tabular-nums' }}>
                                {clinicSum > 0 ? `약 ${fmt(clinicSum)}원` : ''}
                            </span>
                        </div>
                        {open && list.map(s => (
                            <div key={s.caseKey} style={{ padding: '12px 16px', borderTop: '1px solid #f8fafc' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{s.patientName || '환자미상'}</span>
                                    {s.teeth ? <span style={{ fontSize: 12.5, color: '#64748b' }}>#{s.teeth}</span> : null}
                                    {s.prosthesisLabel ? <span style={{ fontSize: 12.5, color: '#475569' }}>{s.prosthesisLabel}</span> : null}
                                    <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 13.5, color: s.estimate == null ? '#9ca3af' : '#7c2d12', fontVariantNumeric: 'tabular-nums' }}>
                                        {s.estimate == null ? '단가 미상' : `약 ${fmt(s.estimate)}원`}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {DISPOSITIONS.map(d => (
                                        <button key={d.key} disabled={saving === s.caseKey}
                                            onClick={() => saveDisposition(s, d.key)}
                                            style={{
                                                padding: '5px 12px', borderRadius: 7, border: `1px solid ${d.color}33`,
                                                background: '#fff', color: d.color, fontSize: 12.5, fontWeight: 600,
                                                cursor: saving === s.caseKey ? 'default' : 'pointer', opacity: saving === s.caseKey ? 0.5 : 1,
                                            }}>
                                            {d.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    );
                })
            )}

            {/* 처리됨 섹션 */}
            {processed.length > 0 && (
                <div style={{ marginTop: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>처리됨 {processed.length}건</div>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                        {processed.map(s => {
                            const rv = reviewMap.get(s.caseKey);
                            return (
                                <div key={s.caseKey} style={{ padding: '10px 16px', borderTop: '1px solid #f8fafc', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{s.clinicName}</span>
                                    <span style={{ fontWeight: 600, fontSize: 13.5, color: '#475569' }}>{s.patientName || '환자미상'}</span>
                                    {s.teeth ? <span style={{ fontSize: 12, color: '#94a3b8' }}>#{s.teeth}</span> : null}
                                    <span style={badge('#eef2ff', '#4338ca')}>{DISPOSITION_LABEL[rv?.disposition] || rv?.disposition}</span>
                                    <button disabled={saving === s.caseKey} onClick={() => undoDisposition(s)}
                                        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#6366f1', fontSize: 12, cursor: 'pointer' }}>
                                        <RotateCcw size={12} /> 되돌리기
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 16, lineHeight: 1.7 }}>
                <AlertCircle size={12} style={{ verticalAlign: '-1px' }} /> 리메이크·아직 작업 중인 케이스, 이 치과로 이 기간에 명세서를 한 번도 발행하지 않은 경우(종이 청구 등)는 목록에서 자동으로 빠집니다.
                금액은 저장된 단가표로 계산한 <b>추정치</b>입니다.
            </p>
        </div>
    );
}

const inputS = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5 };
const badge = (bg, color) => ({ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 6, padding: '2px 8px' });

export default MissingClaims;
